import { useState, useCallback } from "react";
import * as Database from "utils/database.utils";

export type BbgData = {
    name: string;
    isin: string;
    sedol: string;
    cusip: string;
    eu_composite_ticker: string;
};

export type BbgStatus = "idle" | "loading" | "success" | "error";

type FetchResult =
    | { success: true; data: BbgData }
    | { success: false; error: string };

export const useBbgFetch = () => {
    const [status, setStatus] = useState<BbgStatus>("idle");

    const fetch = useCallback(
        async (ticker: string, companyId?: string): Promise<FetchResult> => {
            if (!ticker.trim()) {
                return { success: false, error: "Ticker is required" };
            }

            setStatus("loading");

            try {
                const payload: { ticker: string; company_id?: string } = {
                    ticker,
                };
                if (companyId) {
                    payload.company_id = companyId;
                }

                const res = await Database.post(
                    "api/company/bbg-details",
                    payload
                );

                if (res.ok) {
                    const data = await res.json();
                    setStatus("success");
                    return { success: true, data };
                } else {
                    const errorData = await res.json();
                    setStatus("error");
                    const errorMessage =
                        typeof errorData.error === "string"
                            ? errorData.error
                            : errorData.error?.ticker?.[0] ||
                              "Failed to fetch from Bloomberg";
                    return { success: false, error: errorMessage };
                }
            } catch {
                setStatus("error");
                return {
                    success: false,
                    error: "Failed to connect to Bloomberg",
                };
            }
        },
        []
    );

    const reset = useCallback(() => setStatus("idle"), []);

    return { status, setStatus, fetch, reset };
};
